import type { IBoltConnect, IClientCartCallbacks } from "@boltpay/checkout-api/BoltConnect";
import { IShopifyWindow, ShopifyCart } from "./internal/shopify-types";
import { mapShopifyCartToKlaviyoCheckout } from "./internal/klaviyo";
import { clearCart, debounce, getCartToken, getPdpInfo } from "./internal/utils";

declare global {
	interface Window {
		BoltCheckout: IBoltConnect,
		Shopify: IShopifyWindow;
		// https://developers.klaviyo.com/en/docs/introduction_to_the_klaviyo_object#supported-methods
		klaviyo?: {
			track(event: string, properties?: object, callback?: () => void): Promise<boolean>
			identify(properties: object, callback?: () => void): Promise<object>
		}
		// https://docs.northbeam.io/docs/third-party-checkouts
		Northbeam?: {
			identify(type: "email", value: string): void
		}
	}
}
const { boltButtonSelector = `[data-tid*="bolt-checkout-button"]`, dynamicElementsSelector } = document.currentScript!.dataset;

document.addEventListener('DOMContentLoaded', async function() {
	console.log("Configuring Bolt Managed Checkout");
	const cartToken = await getCartToken();
	const hints = {};
	const productCallbacks: IClientCartCallbacks = {
		async onCheckoutStart() {
			if (window.klaviyo) {
				console.debug("Klaviyo SDK detected, sending checkout started");
				try {
					// We don't have a Bolt cart in the callback, so we fetch the Shopify cart which is the checkout storage for Shopify and map from that.
					// If we ever change it so we are not using the Storefront cart to manage the checkout we'll have to change this as well.
					const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
					if (!cartResponse.ok) {
						throw new Error(`Error fetching Shopify cart: ${await cartResponse.text()}`);
					}
					const cart: ShopifyCart = await cartResponse.json();
					const klaviyoCheckout = mapShopifyCartToKlaviyoCheckout(cart);
					const success = await window.klaviyo.track("Checkout Started", klaviyoCheckout);
					console.debug("Klaviyo checkout started success:", success);
				} catch (e) {
					console.error("Error handling klaviyo checkout started:", e);
				}
			}
		},
		async onEmailEnter(email) {
			if (window.klaviyo) {
				try {
					const result = await window.klaviyo.identify({
						"email": email,
					});
					console.debug("Klaviyo email identify result", result);
				} catch (e) {
					console.error("Error handling klaviyo email identify:", e);
				}
			}
			if (window.Northbeam) {
				try {
					window.Northbeam.identify("email", email);
					console.debug("Northbeam email identify sent");
				} catch (e) {
					console.error("Error handling Northbeam email identify:", e);
				}
			}
		},
		async onShippingDetailsComplete(address) {
			if (window.klaviyo) {
				try {
					const result = await window.klaviyo.identify({
						...address
					});
					console.debug("Klaviyo address identify result", result);
				} catch (e) {
					console.error("Error handling klaviyo address identify:", e);
				}
			}
		},
	};
	const checkoutCallbacks = { ...productCallbacks };
	checkoutCallbacks.success = async (_, callback) => {
		await clearCart();
		if (callback) {
			callback();
		}
	}
	const result = window.BoltCheckout.configure({ id: cartToken }, hints, checkoutCallbacks);
	if (!result?.hasBoltButton) {
		console.warn("No Bolt buttons configured on the page");
	}

	try {
		const pdpInfo = getPdpInfo();
		const productCart = {
			currency: window.Shopify.currency.active ?? "USD",
			items: [
				{
					price: 0, // We ignore this
					quantity: pdpInfo.quantity,
					merchantVariantID: pdpInfo.variantId,
				},
			],
		};
		window.BoltCheckout.configureProductCheckout(productCart, hints, productCallbacks);

		document.querySelectorAll<HTMLInputElement>('input[name="quantity"]').forEach(elem => {
			elem.addEventListener("change", () => {
				// The robust logic for correctly fetching information lives in the utils function,
				// so we do not use the quantity field value directly, but instead refetch the PDP info.
				productCart.items[0].quantity = getPdpInfo().quantity;
				window.BoltCheckout.configureProductCheckout(productCart, hints, productCallbacks);
			});
		});
	} catch {
		console.debug("Not on product page");
	}


	if (dynamicElementsSelector) {
		configureDynamicElements(dynamicElementsSelector, hints, checkoutCallbacks);
	}
});

const configureDynamicElements = (dynamicElementsSelector: string, hints: object, callbacks: IClientCartCallbacks) => {
	const dynamicElements = document.querySelectorAll(dynamicElementsSelector);
	console.debug("Watching for dynamic changes on", dynamicElements);

	const debouncedConfigure = debounce(() => {
		console.log("Bolt button might have changed, configuring");
		getCartToken().then(cartToken => {
			window.BoltCheckout.configure({ id: cartToken }, hints, callbacks);
		}).catch(e => {
			console.error(`Error fetching Shopify token: ${e}`);
		});
	}, 50);

	// We have to watch the parents of the dynamic elements, because the observer does not watch for the removal of the element itself
	const targets = Array.from(dynamicElements).map(elem => elem.parentElement ?? document);
	new Set(targets).forEach(elem => {
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type !== "childList") {
					return false;
				}
				// If the node is an element and it contained any Bolt buttons, we have to re-init the checkout to render the button
				if (mutation.target instanceof Element && mutation.target.querySelector(boltButtonSelector)) {
					debouncedConfigure();
				}
			});
		});
		observer.observe(elem, {
			childList: true,
			subtree: true,
		});
	});
}
